/**
 * Prompt references for the v2 LLM API (PIPH-130).
 *
 * Replaces bundled .txt prompts with reference names resolved server-side
 * via the Storage-backed RTDB registry. Content team owns the actual prompt
 * text; the app only passes the reference name.
 */

import type { TemplateMode } from '../components/BatchUpload';

export type ContextPromptOption = {
  id: string;
  label: string;
  /** v2 API reference name. `undefined` means "no context" (i.e., `id: 'none'`). */
  reference?: string;
  /** `true` if the reference is registered in the v2 RTDB registry; `false` means no-op + warn. */
  ready: boolean;
};

/**
 * Select the system prompt reference for a given template mode.
 * Reaction schemes use the `rxn-schemes` draft; single molecules use `workspace`.
 */
export function systemPromptFor(templateMode: TemplateMode): string {
  return templateMode === 'molecule' ? 'workspace' : 'rxn-schemes';
}

export const CONTEXT_PROMPT_OPTIONS: ContextPromptOption[] = [
  { id: 'simple',    label: 'Full Reaction Scheme',   reference: undefined,       ready: true  },
  { id: 'predict',   label: 'Predict Product',        reference: 'rxn-schemes-incomplete', ready: true  },
  { id: 'resonance', label: 'Resonance Structures',   reference: 'rxn-schemes-resonance', ready: true  },
];

/**
 * Resolve a context option id to the v2 API reference name.
 * - `'none'` or unregistered flavors → returns `undefined` (no context sent).
 * - Unregistered-but-planned flavors also log a warning so we know which
 *   ones are still pending content-team migration.
 */
export function resolveContextReference(optionId: string): string | undefined {
  const option = CONTEXT_PROMPT_OPTIONS.find(o => o.id === optionId);
  if (!option) {
    return undefined;
  }
  if (option.reference === undefined) {
    // 'none' — valid no-context selection
    return undefined;
  }
  if (!option.ready) {
    console.warn(
      `[llm-api] Context flavor "${optionId}" (ref: ${option.reference}) is not yet registered in the v2 API. ` +
        `Sending no context prompt.`
    );
    return undefined;
  }
  return option.reference;
}
